import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Ban, UserX } from "lucide-react";
import MiniGridLoader from '@/components/MiniGridLoader';
import { toast } from "sonner";
import { AvatarUpload } from "./AvatarUpload";

interface BlockedUser {
  id: string;
  blocked_user_id: string;
  created_at: string;
  profiles: {
    username: string;
    avatar_url: string | null;

  };
}

interface BlockedUsersListProps {
  userId: string;
}

export const BlockedUsersList = ({
 userId }: BlockedUsersListProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: blockedUsers, isLoading } = useQuery({
    queryKey: ["blocked-users", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blocked_users")
        .select(`
          id,
          blocked_user_id,
          created_at
        `)
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      // Fetch profiles separately
      if (!data || data.length === 0) return [];
      
      const blockedUserIds = data.map(b => b.blocked_user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, username, avatar_url")
        .in("id", blockedUserIds);
      
      if (profilesError) throw profilesError;
      
      // Combine the data
      return data.map(blocked => {
        const profile = profiles?.find(p => p.id === blocked.blocked_user_id);
        return {
          ...blocked,
          profiles: profile || {
            username: "Unknown",
            avatar_url: null,

          },
        };
      }) as BlockedUser[];
    },
  });

  const unblockMutation = useMutation({
    mutationFn: async (blockedUserId: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke("unblock-user", {
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: { blockedUserId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success(t('notifications.toast.user_unblocked'));
      // Invalidate all blocking-related queries
      queryClient.invalidateQueries({ queryKey: ["blocked-users"] });
      queryClient.invalidateQueries({ queryKey: ["is-blocked"] });
      queryClient.invalidateQueries({ queryKey: ["blocking-status"] });
      // Also invalidate friends and leaderboard to refresh immediately
      queryClient.invalidateQueries({ queryKey: ["friends-list"] });
      queryClient.invalidateQueries({ queryKey: ["pending-friend-requests"] });
      queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to unblock user");
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <MiniGridLoader className="w-6 h-6 text-muted-foreground" />
      </div>
    );
  }

  if (!blockedUsers || blockedUsers.length === 0) {
    return (
      <div className="text-center py-8">
        <UserX className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
        <p className="text-sm text-muted-foreground">{t('ui.text.no_blocked_users')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {blockedUsers.map((blocked) => {
        // Safety check to ensure profiles exists
        if (!blocked.profiles) return null;
        
        return (
          <Card key={blocked.id} className="p-4 bg-card/50 backdrop-blur-sm border-border/50">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <AvatarUpload
                  userId={blocked.blocked_user_id}
                  currentAvatarUrl={blocked.profiles.avatar_url}
                  username={blocked.profiles.username}
                  size="sm"
                  showUploadButton={false}
                  clickable={false}
                />
                <div className="min-w-0">
                  <p className="font-medium truncate">
                    @{blocked.profiles.username}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Blocked {new Date(blocked.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => unblockMutation.mutate(blocked.blocked_user_id)}
                disabled={unblockMutation.isPending}
              >
                <Ban className="w-4 h-4 mr-2" />
                Unblock
              </Button>
            </div>
          </Card>
        );
      })}
    </div>
  );
};
